import {
  collection as firebaseCollection,
  doc as firebaseDoc,
  setDoc as firebaseSetDoc
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import serverModeService from './serverModeService';

const AUDIT_COLLECTION = 'auditLogs';
const SKIP_COLLECTIONS = new Set([
  AUDIT_COLLECTION,
  '_syncOperations',
  '_syncTenants',
  'syncOutbox',
  'syncConflicts',
  '__diagnostic__',
]);
const SENSITIVE_KEY = /(password|passwd|secret|token|api.?key|credential|private.?key|authorization|auth.?header|smtp.?pass|client.?secret)/i;
const MAX_STRING = 1800;
const MAX_ARRAY = 40;
const MAX_KEYS = 80;

const pathCollection = (path = '') => String(path || '').split('/').filter(Boolean)[0] || 'system';
const pathEntity = (path = '') => {
  const parts = String(path || '').split('/').filter(Boolean);
  return parts[parts.length - 1] || '';
};

const plainTenantId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value?.id === 'string') return value.id;
  if (typeof value?.path === 'string') {
    const parts = value.path.split('/').filter(Boolean);
    return parts[parts.length - 1] || '';
  }
  return '';
};

const safeValue = (value, depth = 0, seen = new WeakSet()) => {
  if (value == null) return value;
  if (depth > 4) return '[max-depth]';
  if (typeof value === 'string') return value.length > MAX_STRING ? `${value.slice(0, MAX_STRING)}…` : value;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'function') return '[function]';
  if (value instanceof Date) return value.toISOString();
  if (typeof value?.toDate === 'function') {
    try { return value.toDate().toISOString(); } catch (_) {}
  }
  if (typeof value?.path === 'string' && typeof value?.id === 'string') {
    return { id: value.id, path: value.path };
  }
  if (Array.isArray(value)) return value.slice(0, MAX_ARRAY).map((entry) => safeValue(entry, depth + 1, seen));
  if (typeof value === 'object') {
    if (seen.has(value)) return '[circular]';
    seen.add(value);
    const result = {};
    Object.entries(value).slice(0, MAX_KEYS).forEach(([key, entry]) => {
      result[key] = SENSITIVE_KEY.test(key) ? '[REDACTED]' : safeValue(entry, depth + 1, seen);
    });
    seen.delete(value);
    return result;
  }
  return String(value);
};

const inferTenantId = (data = {}) => {
  const direct = plainTenantId(data?.tenantId) || plainTenantId(data?.tenant) || plainTenantId(data?.companyId) || plainTenantId(data?.tenantKey);
  if (direct) return direct;
  return String(serverModeService.getSnapshot()?.tenantId || '');
};

export const recordAuditMutation = async ({ path = '', action = 'update', data = null, meta = {} } = {}) => {
  const collection = pathCollection(path);
  if (!path || SKIP_COLLECTIONS.has(collection)) return false;
  const tenantId = inferTenantId(data || meta || {});
  if (!tenantId) return false;

  const auditRef = firebaseDoc(firebaseCollection(db, AUDIT_COLLECTION));
  const user = auth.currentUser;
  const payload = {
    tenantId,
    tenantKey: tenantId,
    module: collection,
    collection,
    recordId: pathEntity(path),
    entityId: pathEntity(path),
    path,
    action: String(action || 'update'),
    userId: user?.uid || '',
    userEmail: user?.email || '',
    userName: user?.displayName || user?.email || '',
    timestamp: new Date().toISOString(),
    clientTimestamp: new Date().toISOString(),
    source: 'web',
    details: {
      fields: data && typeof data === 'object' && !Array.isArray(data) ? Object.keys(data).slice(0, MAX_KEYS) : [],
      after: safeValue(data),
      ...safeValue(meta || {}),
    },
    userAgent: typeof navigator !== 'undefined' ? String(navigator.userAgent || '').slice(0, 500) : '',
  };

  try {
    const routed = await serverModeService.writeDocument({ path: auditRef.path, action: 'create', data: payload, merge: false });
    if (routed?.handled) return true;
  } catch (_) {
    // Audit logging must never block the operational transaction. Firebase can
    // still queue the audit event when the browser has Firestore persistence.
  }
  try {
    await firebaseSetDoc(auditRef, payload);
    return true;
  } catch (_) {
    return false;
  }
};

export const auditRedact = safeValue;
export default { recordAuditMutation, auditRedact };
